import { action, computed, observable } from 'mobx'
import type {
  BoolAnswer,
  BoolQuestion,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Question answer editor for `Bool` questions.
 */
export class BoolQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<BoolQuestion> {
  /** @inheritDoc */
  readonly type = 'Bool'

  @observable accessor value: boolean | null = null

  /** @inheritDoc */
  @computed get isAnswered() {
    return this.value !== null
  }

  /** @inheritDoc */
  @computed get answer(): BoolAnswer | null {
    return this.value === null ? null : { type: 'Bool', value: this.value }
  }

  /** @inheritDoc */
  set(question: BoolQuestion): void {
    this.value = question.answer?.value ?? null
  }

  /**
   * Responds to the question.
   *
   * @param value
   */
  @action.bound
  respond(value: boolean) {
    this.value = value
    this.notifyAnswerChanged()
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    return this.isAnswered
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['Please pick one.'])
  }

  /** @inheritDoc */
  protected formatTextAnswer(): string {
    // An unanswered bool must not read as "No" in read-only views.
    if (this.value === null) {
      return ''
    }
    return this.value ? 'Yes' : 'No'
  }
}
